import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { DEFAULT_SKILL_CATEGORIES } from '../lib/constants'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [club, setClub] = useState(null)
  const [fighter, setFighter] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else { setProfile(null); setClub(null); setFighter(null); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function loadProfile(userId) {
    const { data: prof } = await supabase.from('profiles').select('*').eq('id', userId).single()
    setProfile(prof)
    if (prof?.club_id) {
      const { data: clubData } = await supabase.from('clubs').select('*').eq('id', prof.club_id).single()
      setClub(clubData)
      if (prof.role === 'fighter') {
        const { data: fighterData } = await supabase.from('fighters').select('*').eq('user_id', userId).maybeSingle()
        setFighter(fighterData)
      }
    }
    setLoading(false)
  }

  async function signIn(email, password) {
    return supabase.auth.signInWithPassword({ email, password })
  }

  async function signUpAdmin(email, password, fullName, clubName) {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) return { error }
    const userId = data.user.id
    const { data: newClub, error: clubErr } = await supabase
      .from('clubs').insert({ name: clubName, slug: clubName.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now() })
      .select().single()
    if (clubErr) return { error: clubErr }
    await supabase.from('profiles').insert({ id: userId, full_name: fullName, club_id: newClub.id, role: 'admin' })
    for (let ci = 0; ci < DEFAULT_SKILL_CATEGORIES.length; ci++) {
      const cat = DEFAULT_SKILL_CATEGORIES[ci]
      const { data: catRow } = await supabase.from('skill_categories')
        .insert({ club_id: newClub.id, name: cat.name, display_order: ci }).select().single()
      if (catRow) {
        for (let ti = 0; ti < cat.techniques.length; ti++) {
          await supabase.from('techniques').insert({ category_id: catRow.id, name: cat.techniques[ti], display_order: ti })
        }
      }
    }
    return { data }
  }

  async function signUpFighter(email, password, fullName, clubId) {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) return { error }
    await supabase.from('profiles').insert({ id: data.user.id, full_name: fullName, club_id: clubId, role: 'fighter' })
    await supabase.from('join_requests').insert({ club_id: clubId, user_id: data.user.id, full_name: fullName })
    return { data, pendingApproval: true }
  }

  async function signOut() { await supabase.auth.signOut() }

  return (
    <AuthContext.Provider value={{
      user, profile, club, fighter, loading,
      isAdmin: profile?.role === 'admin',
      isFighter: profile?.role === 'fighter',
      isPending: profile?.role === 'fighter' && !fighter,
      signIn, signUpAdmin, signUpFighter, signOut,
      reloadProfile: () => user && loadProfile(user.id)
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
